import type { ComponentProps } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";

import { SelectTrigger } from "./SelectTrigger";
import { SelectValue } from "./SelectValue";
import { SelectContent } from "./SelectContent";
import { SelectItem } from "./SelectItem";
import { SelectGroup } from "./SelectGroup";
import { SelectLabel } from "./SelectLabel";
import { SelectSeparator } from "./SelectSeparator";

/**
 * Props for {@link Select}, optionally narrowed to a string-literal union.
 *
 * `TValue` defaults to `string`, which is what Radix reports, so existing usage is unaffected.
 * Supply it to keep a literal union intact through `value` and `onValueChange` — see {@link Select}.
 */
export type SelectProps<TValue extends string = string> = Omit<
  ComponentProps<typeof SelectPrimitive.Root>,
  "value" | "defaultValue" | "onValueChange"
> & {
  value?: TValue;
  defaultValue?: TValue;
  onValueChange?: (value: TValue) => void;
};

/**
 * A select built on Radix, composed from `Select.Trigger` / `Select.Value` / `Select.Content` /
 * `Select.Item`.
 *
 * Radix types the selected value as a plain `string`. Pass a type argument to preserve a
 * string-literal union instead, so consumers don't need a cast or a type guard to feed the value
 * back into typed state:
 *
 * ```tsx
 * type Status = "all" | "open" | "closed";
 *
 * <Select<Status> value={status} onValueChange={setStatus}>
 *   <Select.Trigger><Select.Value /></Select.Trigger>
 *   <Select.Content>
 *     {options.map(({ value, label }) => (
 *       <Select.Item key={value} value={value}>{label}</Select.Item>
 *     ))}
 *   </Select.Content>
 * </Select>
 * ```
 *
 * The narrowing is a compile-time convenience, not a runtime guarantee: it assumes every
 * `Select.Item` carries one of `TValue`'s members. Validate at the boundary when the options come
 * from outside the type (an API response, user input).
 */
const SelectRoot = <TValue extends string = string>(props: SelectProps<TValue>) => (
  <SelectPrimitive.Root data-slot="select" {...props} />
);

export const Select = Object.assign(SelectRoot, {
  Trigger: SelectTrigger,
  Value: SelectValue,
  Content: SelectContent,
  Item: SelectItem,
  Group: SelectGroup,
  Label: SelectLabel,
  Separator: SelectSeparator,
});
